/**
 * Which saved schedules are still to come. The home page uses it to say
 * that the next schedule is out (a physician should not have to look under
 * History for weeks that have not happened yet), and History to mark those
 * schedules "Upcoming". Pure; the page hands it the list from
 * GET /api/schedules and the server's `today` (America/Toronto), never the
 * browser's clock.
 */
import { compareDates, type ISODate } from '../dates/iso.ts';

export interface DatedSchedule {
  id: string;
  name: string;
  startDate: ISODate;
  endDate: ISODate;
  /**
   * A draft is never "upcoming": only admins see it, and it can still be
   * regenerated or discarded. Admins get drafts in the same list, so the
   * status is checked here rather than trusted to every caller.
   */
  status?: 'draft' | 'saved';
}

/**
 * True for a saved schedule whose first day is after today. On its first
 * day (a Monday) it is the current schedule, not an upcoming one.
 */
export function isUpcoming(schedule: DatedSchedule, today: ISODate): boolean {
  return schedule.status !== 'draft' && compareDates(schedule.startDate, today) > 0;
}

/**
 * The upcoming schedule that starts soonest, or null. `excludingId` is the
 * schedule already on screen: a notice pointing at the page the person is
 * reading would say nothing.
 */
export function nextUpcoming<S extends DatedSchedule>(schedules: readonly S[], today: ISODate, excludingId: string | null = null): S | null {
  let best: S | null = null;
  for (const s of schedules) {
    if (s.id === excludingId || !isUpcoming(s, today)) continue;
    if (best === null || compareDates(s.startDate, best.startDate) < 0) best = s;
  }
  return best;
}
